import { isRepoProductFields } from '@src/types/OTC/otcGuards';
import type { OTCRepoFieldsData, RepoDealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import type { FixingIndexMappingMap } from '@src/utils/fixtures/OTC/fixingFeedUtils';
import {
  DayCountConventionEnum,
  DURATION_INPUT_CUSTOM_VALUE,
  EMPTY_OBJECT,
  LoanQuoteTerminationTypeEnum,
  runValidationV1,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { object, string, type InferType } from 'yup-v1-latest';
import type { BaseProductBuilderSliceStrategy } from './BaseProductBuilderSliceStrategy';
import { positiveNumericString } from './validationUtils';

export const RepoProductBuilderSliceStrategy: BaseProductBuilderSliceStrategy<RepoDealProductBuilderRow> = {
  getDefaultValues: (
    existingOTCFieldsForClear?: RepoDealProductBuilderRow['otcFields']
  ): NonNullable<RepoDealProductBuilderRow['otcFields']> => {
    const repoDefaults: OTCRepoFieldsData = {
      productType: 'repo',
      unitRepoAmount: '1',
      termType: LoanQuoteTerminationTypeEnum.Fixed,
      term: null,
      termCustomUnits: undefined,
      termCustomValue: undefined,
      dayCountConvention: undefined,
    };

    if (isRepoProductFields(existingOTCFieldsForClear)) {
      return { ...repoDefaults, ...existingOTCFieldsForClear };
    }

    return repoDefaults;
  },

  buildNoSuffixInstrumentName: (updatedRow: WritableDraft<RepoDealProductBuilderRow>): string => {
    if (updatedRow?.baseAsset) {
      let instrument = `REPO-${updatedRow.baseAsset}`;
      if (updatedRow.collateralAsset) {
        instrument += `-${updatedRow.collateralAsset}`;
      }
      if (updatedRow.otcFields?.term) {
        instrument += `-${updatedRow.otcFields.term}`;
      }
      return instrument;
    }
    return '';
  },

  updateGridRowData: (state: WritableDraft<RepoDealProductBuilderRow>, rowData: RepoDealProductBuilderRow): void => {
    state.baseAsset = rowData.baseAsset;
    state.collateralAsset = rowData.collateralAsset;
    state.productTemplate = rowData.productTemplate;
    state.uniqueSuffix = rowData.uniqueSuffix;
  },

  updateOTCFields: (
    state: WritableDraft<RepoDealProductBuilderRow>,
    otcFields: RepoDealProductBuilderRow['otcFields']
  ): void => {
    state.otcFields = otcFields == null ? otcFields : { ...otcFields };
  },

  validateFormState: (
    currentRow: RepoDealProductBuilderRow,
    _fixingIndexMapping?: FixingIndexMappingMap
  ): ReturnType<typeof runValidationV1> => {
    return runValidationV1(repoProductBuilderSchema, currentRow, { multipleErrorMode: 'inline' });
  },

  validateOTCFields: (otcFields: RepoDealProductBuilderRow['otcFields']): ReturnType<typeof runValidationV1> => {
    return runValidationV1(repoOtcFieldsSchema, otcFields ?? EMPTY_OBJECT, { multipleErrorMode: 'inline' });
  },
};

/** UI-side validation schema for Repo OTC Fields form */
export const repoOtcFieldsSchema = object().shape({
  productType: string()
    .oneOf(['repo'] as const)
    .required('Product type is required'),
  unitRepoAmount: positiveNumericString('Unit repo amount'),
  termType: string()
    .oneOf([LoanQuoteTerminationTypeEnum.Fixed, LoanQuoteTerminationTypeEnum.OpenTerm] as const)
    .required('Term Type is required'),
  term: string().when('termType', {
    is: LoanQuoteTerminationTypeEnum.Fixed,
    then: schema => schema.required('Term is required'),
    otherwise: schema => schema.nullable().optional(),
  }),
  dayCountConvention: string()
    .oneOf(Object.values(DayCountConventionEnum), 'Invalid Day Count Convention')
    .required('Day Count Convention is required'),
  termCustomUnits: string().when('term', {
    is: DURATION_INPUT_CUSTOM_VALUE,
    then: schema => schema.required('Custom term units are required'),
    otherwise: schema => schema.optional(),
  }),
  termCustomValue: string().when('term', {
    is: DURATION_INPUT_CUSTOM_VALUE,
    then: schema => schema.required('Custom term value is required'),
    otherwise: schema => schema.optional(),
  }),
});

export const repoProductBuilderSchema = object({
  productTemplate: string().required('Product Template is required'),
  product: string()
    .oneOf(['repo'] as const)
    .required('Product is required'),
  baseAsset: string()
    .required('Borrowed Asset is required')
    .test('asset-mismatch', 'Repo Asset and Borrowed Asset cannot be the same', function (value) {
      const { collateralAsset } = this.parent;
      if (!value || !collateralAsset) {
        return true;
      }
      return value !== collateralAsset;
    }),
  collateralAsset: string()
    .required('Repo Asset is required')
    .test('asset-mismatch', 'Repo Asset and Borrowed Asset cannot be the same', function (value) {
      const { baseAsset } = this.parent;
      if (!value || !baseAsset) {
        return true;
      }
      return value !== baseAsset;
    }),
  uniqueSuffix: string().nullable(),
  otcFields: repoOtcFieldsSchema.required('OTC fields are required'),
});

export type ValidatedRepoRowData = InferType<typeof repoProductBuilderSchema>;
